/**
 * Keep Zepto Product Personalizer add-on fees in step with their parent line.
 *
 * Problem: engraving is charged as a separate cart line ("Engraving – selected
 * products only"), linked to the bottle by the `_pc_pricing_ref` property.
 * Zepto only reconciles the two when a page loads. Rebuy Smart Cart edits the
 * cart over AJAX, so removing the bottle there leaves the fee behind, and the
 * shopper can check out straight from the drawer with an engraving-only order.
 * Changing the bottle quantity in the drawer leaves the fee quantity wrong too.
 *
 * Fix 1: after any cart mutation settles, remove fee lines whose parent is gone
 *        and set each remaining fee to the quantity Zepto itself would set.
 * Fix 2: hold checkout clicks until that reconciliation has run, so a click
 *        made while a removal is still in flight cannot reach checkout.
 * Fix 3 (GLBLP-1377 follow-up): change the fee in the same request that changes
 *        its bottle, so the cart never holds an orphaned fee at all. Fix 1 left
 *        one for a couple of seconds, and Zepto's own page-load cleanup of it
 *        deletes by line position and then reloads the page: a shopper who added
 *        another product in that time lost the new product or its fee.
 * Fix 4 (same follow-up): check Zepto's by-position changes against the cart as
 *        it is when they are sent. Zepto also runs its cleanup while its own
 *        add is half done (seen after Back to a product page): it reads the fee
 *        before the bottle lands, then deletes "line 1", which by then is the
 *        bottle - or the fee of a bottle that has just landed.
 * Fix 5 (same follow-up): send cart writes one at a time. Two writes in flight
 *        together overwrite each other: Rebuy's cart-attribute update, sent
 *        while Zepto's fee add was in flight, wrote the cart back without the
 *        fee. Zepto then deleted the bottle for having no fee, and the shopper
 *        was left with an empty cart.
 * Fix 6 (same follow-up): after an add, make sure Rebuy shows it. Rebuy can be
 *        left waiting for a cart refresh that never comes (seen after two adds
 *        in quick succession, with this script and rebuy-cart-fix.js both
 *        removed): the products are in the cart, the pop-out stays closed and
 *        the count stays at 0.
 *
 * Mirrors Zepto's own rules (pplr_common.js): a parent line has both
 * `_pc_pricing_ref` and `_pc_pricing_qty_split`; a fee line has only the ref.
 * The split is "variantId_variantId-qty_qty" or a JSON array of qtys, and the
 * fee quantity is parent quantity x per-unit qty.
 */
(function () {
  'use strict';

  var REF = '_pc_pricing_ref';
  var SPLIT = '_pc_pricing_qty_split';
  var SETTLE_MS = 400;
  var MAX_WAIT_MS = 4000;
  var POSITIONAL_WAIT_MS = 15000;
  var TURN_TIMEOUT_MS = 10000;
  var WRITE_GAP_MS = 750;
  var NUDGE_MS = 300;
  var CONFIRM_MS = 1000;
  var READ_ATTEMPTS = 3;
  var CHECKOUT_SELECTOR = '.rebuy-cart__checkout-button, button[name="checkout"], input[name="checkout"], a[href="/checkout"]';
  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;

  var pending = 0;
  var lastMutationAt = 0;
  var ownRequest = false;
  var running = null;
  var settleTimer = null;
  var bypassUntil = 0;
  var turn = Promise.resolve();
  var nudgeTimer = null;

  function log(message) {
    if (window.localStorage && localStorage.getItem('bboxEngravingDebug')) {
      console.log('[bbox engraving] ' + message);
    }
  }

  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return String(url || '').split('?')[0];
    }
  }

  function isMutation(method, url) {
    return String(method || 'GET').toUpperCase() === 'POST' && MUTATION_RE.test(pathOf(url));
  }

  function mutationStarted() {
    pending += 1;
    lastMutationAt = Date.now();
  }

  // cartText is the response body when it is the whole cart (change, update,
  // clear). A cart that is already in step needs no follow-up read.
  function mutationEnded(own, cartText, added) {
    pending = Math.max(0, pending - 1);
    lastMutationAt = Date.now();
    if (own) return;
    if (added) scheduleNudge();
    try {
      var cart = JSON.parse(cartText);
      if (Array.isArray(cart.items) && cart.token && !Object.keys(plan(cart)).length) return;
    } catch (e) { /* not a cart: fall through to the full check */ }
    scheduleReconcile();
  }

  // Run cart writes in the order they were made, each after the one before has
  // been answered. start() returns a promise for the write. A write that never
  // answers gives up its turn after TURN_TIMEOUT_MS.
  function inTurn(start) {
    var write = turn.then(start);
    // A write sent the instant the previous one is answered can still overwrite
    // it (seen with back-to-back adds: each answered 200, one was gone). Rebuy's
    // own queue leaves about 800ms between writes; do the same.
    var answered = write.then(function () {}, function () {}).then(function () { return sleep(WRITE_GAP_MS); });
    turn = Promise.race([answered, sleep(TURN_TIMEOUT_MS)]);
    return write;
  }

  // NUDGE_MS after an add has been answered: if Rebuy has not caught up by
  // itself, refresh its cart and open the pop-out, which is what it does after
  // an add it did notice. Later adds still queued each get their own turn.
  function scheduleNudge() {
    clearTimeout(nudgeTimer);
    nudgeTimer = setTimeout(function () {
      var rebuy = window.Rebuy;
      var cart = rebuy && rebuy.Cart;
      if (!cart || typeof cart.fetchCart !== 'function' || /\/cart\/?$/.test(window.location.pathname)) return;
      var count = function () { return cart.cart && cart.cart.item_count; };
      var before = count();
      var stuck = typeof cart.isCartQuiescent === 'function' && !cart.isCartQuiescent();
      Promise.resolve(cart.fetchCart()).then(function () {
        if (!stuck && count() === before) return;
        log('Rebuy had not picked up the add; cart refreshed');
        if (rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') rebuy.SmartCart.show();
      }, function () {});
    }, NUDGE_MS);
  }

  function isAdd(url) {
    return /\/cart\/add(\.js|\.json)?$/.test(pathOf(url));
  }

  // Track cart mutations from every source: Rebuy and BOGOS use fetch, Zepto
  // uses jQuery (XHR), the theme cart page uses fetch.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && (input.url || input.href)) || '';
    var method = (init && init.method) || (input && input.method) || 'GET';
    if (!isMutation(method, url)) return nativeFetch.apply(this, arguments);

    var own = ownRequest;
    var self = this;
    var args = arguments;
    mutationStarted();
    var send = function () {
      return (own ? Promise.resolve(null) : withCompanions(input, init))
        .then(function (rewritten) { return nativeFetch.apply(self, rewritten || args); });
    };
    // rebuy-cart-fix.js delays BOGOS gift updates and merges them into one
    // request. Making them take turns here would undo that, so they go straight on.
    return (url.indexOf('sca_process_gifts') > -1 ? send() : inTurn(send))
      .then(
        function (response) {
          if (own || isAdd(url)) {
            mutationEnded(own, undefined, isAdd(url));
          } else {
            response.clone().text().then(
              function (text) { mutationEnded(false, text); },
              function () { mutationEnded(false); }
            );
          }
          return response;
        },
        function (error) { mutationEnded(own); throw error; }
      );
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, async) {
    // A synchronous request cannot wait its turn; leave it alone.
    this._bboxCartMutation = async !== false && isMutation(method, url);
    this._bboxCartAdd = isAdd(url);
    return nativeOpen.apply(this, arguments);
  };
  function sendTracked(xhr, args) {
    mutationStarted();
    inTurn(function () {
      return new Promise(function (resolve) {
        xhr.addEventListener('loadend', function () {
          var text;
          try { text = xhr._bboxCartAdd ? undefined : xhr.responseText; } catch (e) { /* non-text responseType */ }
          mutationEnded(false, text, xhr._bboxCartAdd);
          resolve();
        });
        try {
          nativeSend.apply(xhr, args);
        } catch (e) {
          // Aborted by its caller while it waited.
          mutationEnded(false);
          resolve();
        }
      });
    });
  }
  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this;
    var args = arguments;
    if (!xhr._bboxCartMutation) return nativeSend.apply(xhr, args);

    // "line=N&quantity=Q" is how Zepto removes or resizes a line (pplr_common.js,
    // recursive_ajaxdelete / recursive_ajaxupdate). The theme and Rebuy use fetch.
    var positional = typeof body === 'string' && /^line=(\d+)&quantity=(\d+)$/.exec(body);
    if (!positional) return sendTracked(xhr, args);

    log('by-position change held until the cart is quiet: ' + body);
    positionalTarget(Number(positional[1]), Number(positional[2])).then(function (key) {
      if (key === null) {
        log('by-position change dropped, line is no longer out of step: ' + body);
        xhr.abort();
      } else {
        sendTracked(xhr, key ? ['id=' + encodeURIComponent(key) + '&quantity=' + positional[2]] : args);
      }
    });
  };

  function sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function rebuyBusy() {
    var cart = window.Rebuy && window.Rebuy.Cart;
    return !!(cart && typeof cart.isCartQuiescent === 'function' && !cart.isCartQuiescent());
  }

  // Resolve once no cart mutation has been in flight for SETTLE_MS.
  function settled(maxWait) {
    var start = Date.now();
    return (function wait() {
      var quiet = pending === 0 && !rebuyBusy() && Date.now() - lastMutationAt >= SETTLE_MS;
      if (quiet || Date.now() - start > (maxWait || MAX_WAIT_MS)) return Promise.resolve();
      return sleep(100).then(wait);
    }());
  }

  // Once the cart is quiet, resolve to the key of the line a by-position change
  // points at, provided that change is still what Zepto's rules call for: a fee
  // with no bottle or the wrong quantity, or a bottle with no fee. Resolves to
  // null when it is not (drop the change), undefined when the cart is unreadable
  // (send the change as it came).
  function positionalTarget(line, quantity) {
    function outOfStep(cart, item) {
      var props = item.properties || {};
      var bottleWithoutFee = quantity === 0 && props[REF] && props[SPLIT] && !cart.items.some(function (other) {
        var otherProps = other.properties || {};
        return otherProps[REF] === props[REF] && !otherProps[SPLIT];
      });
      return plan(cart)[item.key] === quantity || bottleWithoutFee;
    }
    return settled(POSITIONAL_WAIT_MS).then(readCart).then(function (first) {
      var item = first.items[line - 1];
      if (!item || !outOfStep(first, item)) return null;
      // As in confirmedPlan: a second read must agree before a line is removed.
      return sleep(CONFIRM_MS).then(function () { return settled(POSITIONAL_WAIT_MS); }).then(readCart).then(function (second) {
        var same = second.items.filter(function (other) { return other.key === item.key; })[0];
        return same && outOfStep(second, same) ? item.key : null;
      });
    }).catch(function () { return undefined; });
  }

  function perUnitQuantity(split, feeVariantId, position) {
    var quantities;
    var variants = [];
    if (split.indexOf('-') > -1) {
      variants = split.split('-')[0].split('_');
      quantities = split.split('-')[1].split('_');
    } else {
      quantities = JSON.parse(split);
    }
    var index = variants.indexOf(String(feeVariantId));
    var value = Number(index > -1 ? quantities[index] : quantities[position]);
    return isNaN(value) ? null : value;
  }

  // Returns { key: quantity } for every fee line that needs changing.
  function plan(cart) {
    var parents = {};
    var fees = {};
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (!props[REF]) return;
      var bucket = props[SPLIT] ? parents : fees;
      (bucket[props[REF]] = bucket[props[REF]] || []).push(item);
    });

    var updates = {};
    Object.keys(fees).forEach(function (ref) {
      var parentLines = parents[ref];
      fees[ref].forEach(function (fee, position) {
        if (!parentLines) {
          updates[fee.key] = 0;
          return;
        }
        var expected = 0;
        for (var i = 0; i < parentLines.length; i++) {
          var perUnit;
          try {
            perUnit = perUnitQuantity(parentLines[i].properties[SPLIT], fee.variant_id, position);
          } catch (e) {
            return; // Unreadable split: leave this fee alone rather than guess.
          }
          if (perUnit === null) return;
          expected += parentLines[i].quantity * perUnit;
        }
        if (expected !== fee.quantity) updates[fee.key] = expected;
      });
    });
    return updates;
  }

  // The quantities a JSON cart request sets, as { line item key: quantity }.
  function requestedQuantities(body, cart) {
    var wanted = {};
    var byKey = {};
    cart.items.forEach(function (item) { byKey[item.key] = item; });
    if (body.updates) {
      Object.keys(body.updates).forEach(function (key) {
        if (byKey[key]) wanted[key] = Number(body.updates[key]);
      });
    } else {
      var item = body.line ? cart.items[Number(body.line) - 1] : byKey[body.id];
      if (item) wanted[item.key] = Number(body.quantity);
    }
    return wanted;
  }

  // When a request changes the quantity of a bottle that carries a fee, resolve
  // to the same request with the fee quantities added, as one /cart/update.
  // Resolves to null (send the request untouched) for everything else: other
  // products, form posts, attribute updates, anything it cannot read.
  function withCompanions(input, init) {
    return new Promise(function (resolve) {
      var body = JSON.parse(init.body);
      var isMap = body.updates && typeof body.updates === 'object' && !Array.isArray(body.updates);
      if (typeof input !== 'string' || (!isMap && body.quantity === undefined)) return resolve(null);

      resolve(readCart().then(function (cart) {
        var wanted = requestedQuantities(body, cart);
        var refs = {};
        var simulated = {
          items: cart.items.map(function (item) {
            if (!(item.key in wanted)) return item;
            var props = item.properties || {};
            if (props[REF] && props[SPLIT]) refs[props[REF]] = true;
            return Object.assign({}, item, { quantity: wanted[item.key] });
          })
        };
        var fees = plan(simulated);
        var updates = isMap ? Object.assign({}, body.updates) : wanted;
        var added = false;
        cart.items.forEach(function (item) {
          var ref = (item.properties || {})[REF];
          // Only fees of the bottles this request changes, and never a fee the
          // request sets itself.
          if (!(item.key in fees) || item.key in wanted || !refs[ref]) return;
          updates[item.key] = fees[item.key];
          added = true;
        });
        if (!added || Object.keys(updates).some(function (key) { return isNaN(updates[key]); })) return null;

        var next = Object.assign({}, body, { updates: updates });
        delete next.line;
        delete next.id;
        delete next.quantity;
        log('fee sent with its bottle ' + JSON.stringify(updates));
        return [
          input.replace(/\/cart\/change(?=\.js|\.json|\?|$)/, '/cart/update'),
          Object.assign({}, init, { body: JSON.stringify(next) })
        ];
      }));
    }).catch(function (error) {
      log('request left as sent: ' + error);
      return null;
    });
  }

  // Read the cart over XHR, not fetch: rebuy-cart-fix.js caches fetch reads of
  // /cart.js and does not see Zepto's XHR adds, so a fetch read can return a
  // cart from between Zepto's two add requests (fee present, bottle not yet).
  function readCartOnce() {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      nativeOpen.call(xhr, 'GET', '/cart.js?_=' + Date.now());
      xhr.setRequestHeader('Cache-Control', 'no-cache');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = function () {
        try {
          if (xhr.status !== 200) throw new Error('status ' + xhr.status);
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          reject(new Error(e.message + ' body: ' + String(xhr.responseText).slice(0, 120).replace(/\s+/g, ' ')));
        }
      };
      xhr.onerror = function () { reject(new Error('network error')); };
      nativeSend.call(xhr);
    });
  }

  // A throttle or challenge page can stand in for the JSON now and then; retry
  // with backoff before giving up.
  function readCart() {
    var attempt = 0;
    return (function tryRead() {
      attempt += 1;
      return readCartOnce().catch(function (error) {
        log('cart read ' + attempt + ' failed: ' + error.message);
        if (attempt >= READ_ATTEMPTS) throw error;
        return sleep(800 * attempt).then(tryRead);
      });
    }());
  }

  // Only act on changes that two reads CONFIRM_MS apart agree on, taken with no
  // cart mutation in between. A transient mid-add state never survives this.
  function confirmedPlan() {
    return settled()
      .then(readCart)
      .then(function (first) {
        var firstUpdates = plan(first);
        if (!Object.keys(firstUpdates).length) return { updates: {}, cart: first };
        return sleep(CONFIRM_MS).then(settled).then(readCart).then(function (second) {
          var updates = plan(second);
          Object.keys(updates).forEach(function (key) {
            if (firstUpdates[key] !== updates[key]) delete updates[key];
          });
          return { updates: updates, cart: second };
        });
      });
  }

  function reconcile() {
    if (running) return running;
    running = confirmedPlan()
      .then(function (result) {
        var cart = result.cart;
        var updates = result.updates;
        var keys = Object.keys(updates);
        if (!keys.length) return { changed: false, cart: cart };

        log('updating ' + JSON.stringify(updates));
        ownRequest = true;
        var request = window.fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
        ownRequest = false;
        return request
          .then(function (response) { return response.json(); })
          .then(function (updated) {
            refreshCartUi();
            return { changed: true, cart: updated };
          });
      })
      .catch(function (error) {
        log('reconcile failed: ' + error);
        return { changed: false, cart: null };
      })
      .then(function (result) {
        running = null;
        return result;
      });
    return running;
  }

  function scheduleReconcile() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(reconcile, SETTLE_MS);
  }

  function refreshCartUi() {
    if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
      window.Rebuy.Cart.fetchCart();
    }
    // The theme cart page renders server-side; reload it the way Zepto does.
    if (/\/cart\/?$/.test(window.location.pathname)) window.location.reload();
  }

  // Capture on window so this runs before the Rebuy and BOGOS checkout handlers.
  window.addEventListener('click', function (event) {
    var target = event.target && event.target.closest && event.target.closest(CHECKOUT_SELECTOR);
    if (!target || Date.now() < bypassUntil) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    log('checkout held');

    // Always a fresh pass: one already under way may have read the cart before
    // the shopper's last change.
    (running || Promise.resolve()).then(reconcile).then(function (result) {
      var empty = result.cart && result.cart.item_count === 0;
      if (empty) {
        log('cart empty after reconcile, checkout cancelled');
        return;
      }
      log('checkout released');
      bypassUntil = Date.now() + 2000;
      target.click();
    });
  }, true);

  // The /cart page waits 300ms after a quantity or bin click before it sends
  // anything. Count the click as cart activity, so a checkout click made in that
  // gap waits for the request instead of reading a cart that is about to change.
  document.addEventListener('change', function (event) {
    if (event.target && event.target.name === 'updates[]') lastMutationAt = Date.now();
  }, true);

  document.addEventListener('rebuy:smartcart.line-item-removed', scheduleReconcile);
  document.addEventListener('rebuy:smartcart.line-item-decrease', scheduleReconcile);
  document.addEventListener('rebuy:smartcart.line-item-increase', scheduleReconcile);
}());
